/**
 * VIDEO モジュール
 * - 画面に少しでも入ったら初めてロード＆再生
 * - 画面幅に応じて PC/SP を自動切替（リサイズ対応）
 */
export const VIDEO = {
  init() {
    const container = document.querySelector('.js-video')
    if (!container) return

    // -------- 設定 --------
    const BREAKPOINT = 768
    const ROOT_MARGIN = '200px 0px' // 早めに読み込みたい場合は増やす
    const UNLOAD_WHEN_OUT = container.dataset.unload === 'true' // 外れたらソース外す（任意）

    // -------- 要素生成（src はまだ付けない）--------
    const video = document.createElement('video')
    video.className = 'c-video'
    video.muted = true
    video.playsInline = true
    video.loop = true
    video.preload = 'none'
    // 既定ポスター（必要に応じて差し替え可）
    video.poster = '/wp-content/themes/rdi25/assets/img/common/bg01.webp'

    const source = document.createElement('source')
    source.type = 'video/mp4'
    video.appendChild(source)
    container.appendChild(video)

    // 現在のバリアント（sp/pc）を判定
    let variant = window.innerWidth < BREAKPOINT ? 'sp' : 'pc'
    setDataSrc(variant)

    // -------- IntersectionObserver で遅延ロード --------
    const io =
      'IntersectionObserver' in window
        ? new IntersectionObserver(onIntersect, { threshold: 0.01, rootMargin: ROOT_MARGIN })
        : null

    if (io) {
      io.observe(video)
    } else {
      // フォールバック：即時ロード＆再生
      loadIfNeeded()
      tryPlay()
    }

    // -------- リサイズでソース切替 --------
    let resizeTimer = 0
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        const next = window.innerWidth < BREAKPOINT ? 'sp' : 'pc'
        if (next !== variant) {
          variant = next
          // 画面内なら即切替、画面外なら data-src だけ更新
          const inView = isInViewport(video)
          setDataSrc(variant, /* overwrite */ true)
          if (inView) {
            loadIfNeeded(true) // 強制再ロード
            tryPlay()
          }
        }
      }, 150)
    })

    // タブ非表示時は一時停止、復帰時は見えていれば自動再生
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (!video.paused) video.pause()
      } else {
        if (isInViewport(video)) tryPlay()
      }
    })

    // ===== ヘルパー =====
    function onIntersect(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          loadIfNeeded()
          tryPlay()
        } else {
          if (!video.paused) video.pause()
          if (UNLOAD_WHEN_OUT) unload()
        }
      })
    }

    function loadIfNeeded(force = false) {
      const loaded = video.dataset._loaded === '1'
      if (force || !loaded) {
        // data-src → src へ移す
        if (!source.src && source.dataset.src) {
          source.src = source.dataset.src
        }
        // <video> 直下の src 属性は未使用なので消しておく（保険）
        video.removeAttribute('src')
        video.load()
        video.dataset._loaded = '1'
      }
    }

    function unload() {
      video.pause()
      video.removeAttribute('src')
      source.removeAttribute('src') // data-src は残す
      try {
        video.load()
      } catch (_) {}
      video.dataset._loaded = ''
    }

    function tryPlay() {
      // iOS/モバイルの自動再生条件を満たすように
      if (!video.muted) video.muted = true
      if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '')
      // autoplay 属性は付けず、JS から制御
      video.play().catch(() => {
        // ブロックされた場合は、初回ユーザー操作で再生
        const once = () => {
          video.play().finally(() => {
            video.removeEventListener('pointerdown', once)
            video.removeEventListener('touchstart', once)
            video.removeEventListener('click', once)
          })
        }
        video.addEventListener('pointerdown', once, { once: true })
        video.addEventListener('touchstart', once, { once: true, passive: true })
        video.addEventListener('click', once, { once: true })
      })
    }

    function isInViewport(el) {
      const r = el.getBoundingClientRect()
      const h = window.innerHeight || document.documentElement.clientHeight
      return r.bottom > 0 && r.top < h
    }

    function srcFor(v) {
      return v === 'sp'
        ? '/wp-content/themes/rdi25/assets/img/common/bg01_sp.mp4'
        : '/wp-content/themes/rdi25/assets/img/common/bg01_pc.mp4'
    }

    function setDataSrc(v, overwrite = false) {
      const next = srcFor(v)
      if (overwrite || source.dataset.src !== next) {
        source.dataset.src = next
        // 既にロード済みかつ overwrite の場合は src を差し替える
        if (overwrite && video.dataset._loaded === '1') {
          source.src = next
        }
      }
    }
  },
}
